import { useCallback } from 'react';
import type { StepperAnswers } from './useMatchmakingStateManagement';
import type { HistoryItem } from '../components/MatchmakingHistoryPopover';

type UseMatchmakingHandlersProps = {
  matchingPrompt: string;
  hasClients: boolean;
  selectedClientId: string | null;
  isViewingHistoryResults: boolean;
  lastPromptRef: React.MutableRefObject<string>;
  lastHistoryItemRef: React.MutableRefObject<HistoryItem | null>;
  setIsLocationModalOpen: (open: boolean) => void;
  fetchHistoryResults: (historyItem: HistoryItem, isLoadMore?: boolean) => Promise<void>;
  executeMatchSearch: (isLoadMore: boolean, answers?: StepperAnswers) => Promise<void>;
  setStepperAnswers: (answers: StepperAnswers) => void;
  matchmakingState: { setClientQuery: (value: string) => void };
  selectedClientIdValue: string | null;
};

export const useMatchmakingHandlers = ({
  matchingPrompt,
  hasClients,
  selectedClientId,
  isViewingHistoryResults,
  lastPromptRef,
  lastHistoryItemRef,
  setIsLocationModalOpen,
  fetchHistoryResults,
  executeMatchSearch,
  setStepperAnswers,
  matchmakingState,
  selectedClientIdValue,
}: UseMatchmakingHandlersProps) => {
  const handleGenerateMatches = useCallback(
    async (isLoadMore = false) => {
      if (!matchingPrompt.trim() || !hasClients || !selectedClientId) {
        return;
      }
      lastPromptRef.current = matchingPrompt;
      if (isLoadMore) {
        if (isViewingHistoryResults && lastHistoryItemRef.current) {
          await fetchHistoryResults(lastHistoryItemRef.current, true);
          return;
        }
        await executeMatchSearch(true);
        return;
      }
      setIsLocationModalOpen(true);
    },
    [
      matchingPrompt,
      hasClients,
      selectedClientId,
      isViewingHistoryResults,
      executeMatchSearch,
      fetchHistoryResults,
      setIsLocationModalOpen,
      lastPromptRef,
      lastHistoryItemRef,
    ],
  );

  const handleFindMatches = useCallback(
    (answers: StepperAnswers) => {
      setStepperAnswers(answers);
      setIsLocationModalOpen(false);
      executeMatchSearch(false, answers);
    },
    [executeMatchSearch, setStepperAnswers, setIsLocationModalOpen],
  );

  const handlePromptChange = useCallback(
    (value: string) => {
      if (selectedClientIdValue) {
        matchmakingState.setClientQuery(value);
      }
    },
    [selectedClientIdValue, matchmakingState],
  );

  const handleSelectHistory = useCallback(
    (historyItem: HistoryItem, advisorProfileId: string | null) => {
      if (selectedClientId && advisorProfileId) {
        fetchHistoryResults(historyItem);
      }
    },
    [selectedClientId, fetchHistoryResults],
  );

  return {
    handleGenerateMatches,
    handleFindMatches,
    handlePromptChange,
    handleSelectHistory,
  };
};

